import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Button, Group, LoadingOverlay, Modal, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { Icon, LoadMorePagination } from '@/components';
import type { Keyword, KeywordCreateRequest, KeywordUpdateRequest } from '@/types/keywords';
import { ShortCodeModal } from './ShortCodeModal';
import styles from './ShortCodes.module.sass';
import {
  useCreateKeywordMutation,
  useDeleteKeywordMutation,
  useKeywords,
  useUpdateKeywordMutation,
} from '@/hooks/keywords';
import { keywordsService } from '@/services/keywords.service';

const LAYOUT = {
  TABLE_FILTER_ICON_SIZE: 20,
  SHORT_CODES_PER_PAGE_LIMIT: 10,
};

export const ShortCodes = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<Keyword | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Keyword | null>(null);
  const [extraItems, setExtraItems] = useState<Keyword[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const queryParams = useMemo(() => ({ limit: LAYOUT.SHORT_CODES_PER_PAGE_LIMIT }), []);

  const { data, isLoading } = useKeywords(queryParams);
  const createMutation = useCreateKeywordMutation();
  const updateMutation = useUpdateKeywordMutation();
  const deleteMutation = useDeleteKeywordMutation();

  const firstPageItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const items = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);
  const hasMore = items.length < total;

  useEffect(() => {
    setExtraItems([]);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [data]);

  const handleOpenNew = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: Keyword) => {
    setEditing(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item: Keyword) => {
    setConfirmDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    await deleteMutation.mutateAsync(confirmDelete.id);
    setConfirmDelete(null);
  };

  const handleSave = async (data: KeywordCreateRequest | KeywordUpdateRequest) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
    setIsModalOpen(false);
    setEditing(null);
  };

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await keywordsService.getKeywords({ ...queryParams, cursor });
      setExtraItems((prev) => [...prev, ...response.items]);
      if (response.items.length > 0) {
        setCursor(response.items[response.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more short codes:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <Stack mx="xxl" my="md" gap="md">
      <Group justify="flex-start">
        <Button leftSection={<Icon name="IconPlus" />} onClick={handleOpenNew}>
          Add Short Code
        </Button>
      </Group>

      <Paper radius="md" pos="relative" p="xl">
        <Title order={3} mb="md">
          Short Codes
        </Title>
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover withRowBorders withColumnBorders withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Keyword</Table.Th>
              <Table.Th>Message</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>{item.id}</Table.Td>
                <Table.Td>{item.keyword}</Table.Td>
                <Table.Td>{item.message}</Table.Td>
                <Table.Td className={styles.actionsColumn}>
                  <Group gap="xs">
                    <ActionIcon aria-label="edit" variant="subtle" onClick={() => handleEdit(item)}>
                      <Icon name="IconEdit" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
                    </ActionIcon>
                    <ActionIcon aria-label="delete" variant="subtle" color="red" onClick={() => handleDelete(item)}>
                      <Icon name="IconTrash" size={LAYOUT.TABLE_FILTER_ICON_SIZE} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed">No short codes</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <LoadMorePagination
        loaded={items.length}
        total={total}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <ShortCodeModal
        opened={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditing(null);
        }}
        keyword={editing}
        onSave={handleSave}
      />

      <Modal
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete short code?"
        classNames={{
          header: styles.modalHeader,
          title: styles.modalTitle,
          close: styles.closeButton,
        }}
      >
        <Stack mt="md">
          <Text>Are you sure you want to delete this short code?</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              No
            </Button>
            <Button color="red" onClick={handleConfirmDelete}>
              Yes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};
